import React, { useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CheckIcon, ExclamationTriangleIcon, MinusIcon, PlusIcon } from "@heroicons/react/20/solid";
import { DIFFICULTY_LEVELS, DIFFICULTY_POINTS, DifficultyBadge } from "../../Difficulty/Difficulty";
import { MAX_COUNT, ROMAN, countOf, coverage, hasHours, pointsOf, quarterLabel, standardGaps, subtopicsOf, toNumber, trimName } from "./matrixModel";

export const focusRing = "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600";
export const cardClass = "rounded-2xl border border-slate-200 bg-white shadow-sm";
export const selectClass = `rounded-lg border-slate-300 py-0 pl-2 pr-7 text-sm shadow-sm transition duration-200 hover:border-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-600/15 disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400`;
export const linkButtonClass = `inline-flex items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-40 ${focusRing}`;
// A side "add" action as a text link ("+ Add a row to quarter I", "+ Add a grade"): no frame, 44px tall on phones.
export const addLinkClass = `inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg px-2 text-sm font-medium text-blue-700 transition duration-200 enabled:hover:bg-blue-50 enabled:hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 ${focusRing}`;
// A secondary action beside the main one (Cancel): quiet text from md up; on phones a framed button sharing the
// row with the main one, both 44px tall.
export const secondaryButtonClass = `inline-flex h-10 items-center justify-center gap-1.5 whitespace-nowrap rounded-lg px-3 text-sm font-medium text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-50 max-md:h-11 max-md:flex-1 max-md:bg-white max-md:text-slate-700 max-md:shadow-sm max-md:ring-1 max-md:ring-inset max-md:ring-slate-300 ${focusRing}`;

export function Spinner({ className = "h-5 w-5" }) {
  return (
    <span
      className={`inline-block shrink-0 animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 ${className}`}
      aria-hidden="true"
    />
  );
}

// A dash in a cell; `srText` is what it says to a screen reader (a count of 0, else e.g. "not set").
export const Dash = ({ srText = "0" }) => (
  <>
    <span aria-hidden="true" className="text-slate-300">
      —
    </span>
    <span className="sr-only">{srText}</span>
  </>
);

// "12 тапсырма · 58 балл".
export const tasksAndPoints = (t, counts, points) =>
  `${t("folders.matrix.tasks", { count: countOf(counts) })} · ${t("difficulty.points", { count: pointsOf(counts, points) })}`;

/**
 * The grades of a matrix as tabs (`tabs`: [{grade, label, mark: "ok" | "off" | null, markText}]); the arrow keys,
 * Home and End move between them. `idBase` names the tabs and their panel (`${idBase}-panel`); `children` follow
 * the tabs (the edit mode's "Add a grade").
 */
export function ClassTabs({ tabs, selected, onSelect, idBase, children }) {
  const [t] = useTranslation();
  const refs = useRef(new Map());
  const move = (event, index) => {
    const keys = { ArrowRight: index + 1, ArrowLeft: index - 1, Home: 0, End: tabs.length - 1 };
    if (!(event.key in keys)) return;
    event.preventDefault();
    const tab = tabs[(keys[event.key] + tabs.length) % tabs.length];
    onSelect(tab.grade);
    refs.current.get(tab.grade)?.focus();
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div
        role="tablist"
        aria-label={t("folders.matrix.classes_label")}
        className="flex gap-2 max-sm:-mx-4 max-sm:overflow-x-auto max-sm:px-4 max-sm:pb-1 sm:flex-wrap"
      >
        {tabs.map((tab, index) => {
          const current = tab.grade === selected;
          return (
            <button
              key={tab.grade}
              ref={(el) => (el ? refs.current.set(tab.grade, el) : refs.current.delete(tab.grade))}
              type="button"
              role="tab"
              id={`${idBase}-tab-${tab.grade}`}
              aria-selected={current}
              aria-controls={`${idBase}-panel`}
              tabIndex={current ? 0 : -1}
              onClick={() => onSelect(tab.grade)}
              onKeyDown={(event) => move(event, index)}
              className={`inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 text-sm ring-1 ring-inset transition duration-200 max-md:h-11 ${focusRing} ${
                current
                  ? "bg-blue-50 font-semibold text-blue-700 ring-blue-600"
                  : "bg-white font-medium text-slate-700 ring-slate-200 hover:bg-slate-50 hover:ring-slate-300"
              }`}
            >
              {tab.label}
              {tab.mark === "ok" && <CheckIcon className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
              {tab.mark === "off" && <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />}
              {tab.markText && <span className="sr-only">{tab.markText}</span>}
            </button>
          );
        })}
      </div>
      {children}
    </div>
  );
}

// A quarter's heading: its name, its role in the grade ("for the previous / current quarter") and what it plans.
export function QuarterHeading({ id, quarter, role, summary, children }) {
  const [t] = useTranslation();
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 pb-2">
      <h3 id={id} className="text-[15px] font-semibold text-slate-900">
        {quarterLabel(t, quarter)}
      </h3>
      {role && (
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
            role === "current" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"
          }`}
        >
          {t(`folders.matrix.role_${role}`)}
        </span>
      )}
      {summary && <span className="ml-auto text-sm tabular-nums text-slate-600">{summary}</span>}
      {children}
    </div>
  );
}

// How many of a row's subtopics show before "+N".
const SUBTOPICS_SHOWN = 3;

// The row's topic over its subtopics, one a line: the first three and a "+N" that shows the rest (all of them when
// not `expandable`, e.g. inside a button). A topic planned whole says so; a row without a topic says it has none.
// `mark` follows a topic whose name another topic of the grade has («код 23», topicMarks).
export function RowName({ row, mark = "", expandable = true }) {
  const [t] = useTranslation();
  const [open, setOpen] = useState(false);
  const listId = useId();
  if (!row.topic) {
    return (
      <span className="flex items-center gap-1.5 text-sm font-medium text-amber-800">
        <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" aria-hidden="true" />
        {t("folders.matrix.no_topic")}
      </span>
    );
  }
  const subtopics = subtopicsOf(row);
  const extra = expandable ? Math.max(0, subtopics.length - SUBTOPICS_SHOWN) : 0;
  return (
    <>
      <span className="block break-words text-sm font-medium text-slate-900">
        {trimName(row.topic.name)}
        {mark && <span className="font-normal text-slate-500"> · {mark}</span>}
      </span>
      {subtopics.length ? (
        <ul id={listId} className="mt-0.5 space-y-0.5 text-xs leading-5 text-slate-500">
          {(open ? subtopics : subtopics.slice(0, subtopics.length - extra)).map((s) => (
            <li key={s.uuid} className="flex gap-1.5">
              <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-slate-400" aria-hidden="true" />
              <span className="min-w-0 break-words">{trimName(s.name)}</span>
            </li>
          ))}
        </ul>
      ) : (
        <span className="mt-0.5 block text-xs text-slate-500">{t("folders.matrix.whole_topic")}</span>
      )}
      {extra > 0 && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={listId}
          aria-label={open ? undefined : t("folders.matrix.more_subtopics", { count: extra })}
          onClick={() => setOpen((value) => !value)}
          className={`ml-1 inline-flex items-center rounded px-1 text-xs font-medium text-blue-700 transition duration-200 hover:bg-blue-50 hover:text-blue-800 max-md:min-h-[2.75rem] max-md:px-2 ${focusRing}`}
        >
          {open ? t("folders.matrix.fewer_subtopics") : `+${extra}`}
        </button>
      )}
    </>
  );
}

// A week as "6-апта", or a dash.
export function WeekText({ week }) {
  const [t] = useTranslation();
  return week ? <span className="whitespace-nowrap">{t("folders.matrix.week_n", { n: week })}</span> : <Dash srText={t("folders.matrix.week_none")} />;
}

// How the bank covers a saved row: enough approved questions, or the places none takes (and how many questions
// written for them are not approved yet). A row without a topic has no questions to count.
export function BankStatus({ row }) {
  const [t] = useTranslation();
  if (!row.topic) return <Dash srText={t("folders.matrix.no_topic")} />;
  const c = coverage(row);
  if (!c.planned && !c.none) return <Dash />;
  let badge = null;
  if (c.missing > 0) {
    const levels = DIFFICULTY_LEVELS.filter((level) => c.short[level]).map((level) => `${level} ${c.short[level]}`).join(", ");
    badge = (
      <span className="inline-flex rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700 ring-1 ring-inset ring-red-600/20">
        {t("folders.matrix.bank_short", { levels })}
      </span>
    );
  } else if (c.planned) {
    badge = (
      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-800 ring-1 ring-inset ring-emerald-600/20">
        <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" />
        {t("folders.matrix.bank_ok")}
      </span>
    );
  }
  return (
    <span className="flex flex-col items-start gap-1">
      {badge}
      {c.unapproved > 0 && <span className="text-xs text-slate-600">{t("folders.matrix.bank_unapproved", { count: c.unapproved })}</span>}
      {c.none > 0 && (
        <span className="inline-flex items-center gap-1 text-xs text-amber-800">
          <ExclamationTriangleIcon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {t("folders.matrix.bank_no_difficulty", { count: c.none })}
        </span>
      )}
    </span>
  );
}

/**
 * A planned count: − / + around the typed number (0–999; the arrow keys step it too). The buttons stay out of
 * the tab order: the field is the stop. Under it, how many approved questions the bank has there (`bank`).
 */
export function Stepper({ value, onChange, level, name, invalid, describedBy, disabled, large = false, bank, focusKey }) {
  const [t] = useTranslation();
  const set = (n) => onChange(String(Math.min(MAX_COUNT, Math.max(0, n))));
  const button = `inline-flex ${large ? "h-11 w-10" : "h-8 w-8"} items-center justify-center bg-slate-50 text-slate-600 transition duration-200 enabled:hover:bg-slate-100 enabled:hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300`;
  const number = toNumber(value);
  return (
    <span className="inline-flex flex-col items-center">
      <span className={`inline-flex overflow-hidden rounded-lg border bg-white shadow-sm ${invalid ? "border-red-400" : "border-slate-300"}`}>
        <button type="button" tabIndex={-1} aria-label={t("folders.matrix.decrease", { level, name })} disabled={disabled || number <= 0} onClick={() => set(number - 1)} className={button}>
          <MinusIcon className="h-4 w-4" aria-hidden="true" />
        </button>
        <input
          type="text"
          inputMode="numeric"
          autoComplete="off"
          data-focus={focusKey}
          value={value}
          placeholder="0"
          disabled={disabled}
          aria-label={t("folders.matrix.planned_input", { level, name })}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
          onFocus={(e) => e.target.select()}
          onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 3))}
          onBlur={() => value !== "" && String(number) !== value && onChange(String(number))}
          onKeyDown={(e) => {
            if (e.key !== "ArrowUp" && e.key !== "ArrowDown") return;
            e.preventDefault();
            set(number + (e.key === "ArrowUp" ? 1 : -1));
          }}
          className={`${large ? "h-11 w-11" : "h-8 w-10"} border-0 border-x border-slate-200 p-0 text-center text-sm tabular-nums focus:ring-2 focus:ring-inset focus:ring-blue-600 disabled:bg-slate-50 ${
            number ? "font-semibold text-slate-900" : "text-slate-500"
          }`}
        />
        <button type="button" tabIndex={-1} aria-label={t("folders.matrix.increase", { level, name })} disabled={disabled || number >= MAX_COUNT} onClick={() => set(number + 1)} className={button}>
          <PlusIcon className="h-4 w-4" aria-hidden="true" />
        </button>
      </span>
      {bank !== undefined && (
        <span className="mt-0.5 text-[11px] tabular-nums text-slate-500">
          <span aria-hidden="true">{bank === null ? t("folders.matrix.bank_hint_unknown") : t("folders.matrix.bank_hint", { count: bank })}</span>
          {bank !== null && <span className="sr-only">{t("folders.matrix.bank_hint_full", { level, count: bank })}</span>}
        </span>
      )}
    </span>
  );
}

/**
 * What a grade's draft plans, kept in view while editing. In a «БТС-N» folder (`standard` = {A: 10, B: 6, C: 4})
 * each level against the standard and what is left to plan; elsewhere just the sums. `compact` (phones): two
 * short lines, the plan against the standard and each level's count.
 */
export function PlanBar({ planned, standard, points = DIFFICULTY_POINTS, compact = false }) {
  const [t] = useTranslation();
  const check = standardGaps(planned, standard);
  const total = countOf(planned);
  const chip = (key, content, tone = "plain") => (
    <span
      key={key}
      className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-sm tabular-nums ring-1 ring-inset ${
        tone === "warn" ? "bg-amber-50 ring-amber-300" : "bg-white ring-slate-200"
      }`}
    >
      {content}
    </span>
  );
  let status = null;
  if (check) {
    const parts = DIFFICULTY_LEVELS.filter((level) => check.gaps[level] !== 0).map((level) =>
      check.gaps[level] < 0
        ? t("folders.matrix.budget_left", { level, count: -check.gaps[level] })
        : t("folders.matrix.budget_extra", { level, count: check.gaps[level] })
    );
    status = check.ok ? (
      <span className="inline-flex items-center gap-1 text-emerald-700">
        <CheckIcon className="h-4 w-4" aria-hidden="true" />
        {t("folders.matrix.budget_ok")}
      </span>
    ) : (
      <span className="text-amber-800">
        {t("folders.matrix.budget_prefix")} {parts.join("; ")}
      </span>
    );
  }
  if (compact) {
    return (
      <div className={`rounded-xl border px-3 py-2 ${check && !check.ok ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
        <p className="text-xs font-semibold tabular-nums text-slate-900">
          {check
            ? t("folders.matrix.standard_progress", {
                planned: total,
                tasks: t("folders.matrix.tasks", { count: countOf(standard) }),
                points: pointsOf(planned, points),
                maxPoints: t("difficulty.points", { count: pointsOf(standard, points) }),
              })
            : t("folders.matrix.plan_line", { plan: tasksAndPoints(t, planned, points) })}
        </p>
        <p className="mt-1.5 flex flex-wrap gap-1.5 text-[13px] tabular-nums">
          {DIFFICULTY_LEVELS.map((level) => {
            const off = check && check.gaps[level] !== 0;
            return (
              <span
                key={level}
                className={`inline-flex items-center gap-1 rounded-lg px-2 py-0.5 ${off ? "bg-amber-100 text-amber-900" : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200"}`}
              >
                <b>{level}</b>
                {check ? `${toNumber(planned[level])}/${toNumber(standard[level])}` : toNumber(planned[level])}
                {check && !off && <CheckIcon className="h-3.5 w-3.5 text-emerald-600" aria-hidden="true" />}
              </span>
            );
          })}
        </p>
        {status && (
          <p className="sr-only" aria-live="polite">
            {status}
          </p>
        )}
      </div>
    );
  }
  return (
    <div className={`flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border px-4 py-3 ${check && !check.ok ? "border-amber-200 bg-amber-50/40" : "border-slate-200 bg-slate-50"}`}>
      <div className="min-w-0 flex-1 basis-56">
        <p className="text-sm font-semibold text-slate-900">
          {check
            ? t("folders.matrix.standard_line", {
                tasks: t("folders.matrix.tasks", { count: countOf(standard) }),
                points: t("difficulty.points", { count: pointsOf(standard, points) }),
              })
            : t("folders.matrix.plan_line", { plan: tasksAndPoints(t, planned, points) })}
        </p>
        {status && (
          <p className="mt-0.5 text-xs" aria-live="polite">
            {status}
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {DIFFICULTY_LEVELS.map((level) =>
          chip(
            level,
            <>
              <DifficultyBadge level={level} showPoints={false} />
              <b className="text-slate-900">{toNumber(planned[level])}</b>
              {check && <span className="text-slate-500">/ {toNumber(standard[level])}</span>}
              {check && check.gaps[level] === 0 && <CheckIcon className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
            </>,
            check && check.gaps[level] !== 0 ? "warn" : "plain"
          )
        )}
        {chip(
          "tasks",
          <>
            {t("folders.matrix.tasks_label")} <b className="text-slate-900">{total}</b>
            {check && <span className="text-slate-500">/ {countOf(standard)}</span>}
          </>
        )}
        {chip(
          "points",
          <>
            {t("folders.matrix.points_label")} <b className="text-slate-900">{pointsOf(planned, points)}</b>
            {check && <span className="text-slate-500">/ {pointsOf(standard, points)}</span>}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone = "plain" }) {
  const tones = {
    plain: "border-slate-200 bg-white",
    ok: "border-emerald-200 bg-emerald-50/60",
    warn: "border-amber-200 bg-amber-50/60",
  };
  return (
    <div className={`rounded-xl border px-4 py-3 ${tones[tone]}`}>
      <dt className={`text-xs font-semibold ${tone === "ok" ? "text-emerald-700" : tone === "warn" ? "text-amber-800" : "text-slate-500"}`}>{label}</dt>
      <dd className="mt-1 text-lg font-semibold tabular-nums text-slate-900">{value}</dd>
      {sub && <dd className="mt-0.5 text-xs text-slate-600">{sub}</dd>}
    </div>
  );
}

/**
 * A grade at a glance: its plan (against the BTS standard in a «БТС-N» folder), each quarter with its role and
 * hours (the server sums a quarter's unset hours as 0: they show only when a row has some), and how the bank
 * covers it. `info` is the grade's entry of the matrix's `grades`; `rows` its rows. The cards share one row.
 */
export function ClassSummary({ info, rows, standard, points }) {
  const [t] = useTranslation();
  const planned = info?.planned || {};
  const check = standardGaps(planned, standard);
  const levels = DIFFICULTY_LEVELS.map((level) => `${level} ${toNumber(planned[level])}`).join(" · ");
  let missing = 0;
  let unapproved = 0;
  rows.forEach((row) => {
    const c = coverage(row);
    missing += c.missing;
    unapproved += c.unapproved;
  });
  const bankSub = [
    missing ? t("folders.matrix.bank_card_missing", { count: missing }) : t("folders.matrix.bank_card_ok"),
    unapproved ? t("folders.matrix.bank_unapproved", { count: unapproved }) : "",
  ]
    .filter(Boolean)
    .join("; ");
  const quarters = (info?.quarters || []).filter((q) => q.quarter);
  return (
    <dl className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,13rem),1fr))] gap-3">
      <Stat
        label={check ? (check.ok ? t("folders.matrix.standard_ok") : t("folders.matrix.standard_off")) : t("folders.matrix.plan_label")}
        value={tasksAndPoints(t, planned, points)}
        sub={check ? t("folders.matrix.standard_compare", { levels, standard: DIFFICULTY_LEVELS.map((l) => `${l} ${toNumber(standard[l])}`).join(" · ") }) : levels}
        tone={check ? (check.ok ? "ok" : "warn") : "plain"}
      />
      {quarters.map((q) => (
        <Stat
          key={q.quarter}
          label={q.role ? `${t(`folders.matrix.role_${q.role}_title`)} · ${ROMAN[q.quarter]}` : quarterLabel(t, q.quarter)}
          value={tasksAndPoints(t, q.planned, points)}
          sub={hasHours(rows.filter((row) => row.quarter === q.quarter)) ? t("folders.matrix.hours_n", { count: toNumber(q.hours) }) : ""}
        />
      ))}
      <Stat label={t("folders.matrix.bank_card_label")} value={t("folders.matrix.questions", { count: toNumber(info?.approved?.count) })} sub={bankSub} tone={missing ? "warn" : "plain"} />
    </dl>
  );
}
